import React, { forwardRef, useState, useEffect } from 'react';
import styles from './Calendar.module.css';

export interface ICalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}

const Calendar = forwardRef<HTMLDivElement, ICalendarProps>(
  ({ value, onChange, minDate, maxDate, className, ...rest }, ref) => {
    const [viewDate, setViewDate] = useState(value || new Date());

    useEffect(() => {
      if (value) {
        setViewDate(value);
      }
    }, [value]);

    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
      // 0 = Sunday, 1 = Monday, etc.
      // We want Monday to be 0 for our grid if we start with Mon
      const day = new Date(year, month, 1).getDay();
      return day === 0 ? 6 : day - 1;
    };

    const handlePrevMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const handleDateClick = (day: number) => {
      const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      if (isDisabled(newDate)) return;
      onChange(newDate);
    };

    const isDisabled = (date: Date) => {
      if (minDate) {
        const min = new Date(minDate);
        min.setHours(0, 0, 0, 0);
        if (date < min) return true;
      }
      if (maxDate) {
        const max = new Date(maxDate);
        max.setHours(23, 59, 59, 999);
        if (date > max) return true;
      }
      return false;
    };

    const isSelected = (day: number) => {
      return (
        value &&
        value.getDate() === day &&
        value.getMonth() === viewDate.getMonth() &&
        value.getFullYear() === viewDate.getFullYear()
      );
    };

    const isToday = (day: number) => {
      const today = new Date();
      return (
        today.getDate() === day &&
        today.getMonth() === viewDate.getMonth() &&
        today.getFullYear() === viewDate.getFullYear()
      );
    };

    const renderDays = () => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();
      const daysInMonth = getDaysInMonth(year, month);
      const firstDay = getFirstDayOfMonth(year, month);
      const days = [];

      // Empty cells for days before the first day of the month
      for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className={`${styles.day} ${styles.empty}`} />);
      }

      // Days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const disabled = isDisabled(date);
        const selected = isSelected(day);
        const today = isToday(day);

        days.push(
          <div
            key={day}
            className={`
            ${styles.day} 
            ${selected ? styles.selected : ''} 
            ${today ? styles.today : ''}
            ${disabled ? styles.disabled : ''}
          `}
            onClick={() => !disabled && handleDateClick(day)}
          >
            {day}
          </div>,
        );
      }

      return days;
    };

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    return (
      <div ref={ref} className={[styles.calendar, className].filter(Boolean).join(' ')} {...rest}>
        <div className={styles.header}>
          <div className={styles.monthYear}>
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
          <div className={styles.navButtons}>
            <button className={styles.navButton} onClick={handlePrevMonth}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button className={styles.navButton} onClick={handleNextMonth}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
        <div className={styles.grid}>
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
            <div key={day} className={styles.dayLabel}>
              {day}
            </div>
          ))}
          {renderDays()}
        </div>
      </div>
    );
  },
);

Calendar.displayName = 'Calendar';

export default Calendar;
